import api from '@constants/endpoints';
import request from '@helpers/request';
import { combine, createEffect, createEvent, createStore } from 'effector';
import { get } from 'lodash';
import createPreloadersStore from './preloaders';
import { ITribesChatDetailMessages, ITribesMembers, ITribes } from '@interfaces/ITribes';

export const reset = createEvent('ChatDetail store reset');

export const getList = createEffect({
  name: 'getList',
  async handler({ chatId, lastMessageId }: { chatId: string; lastMessageId?: string }) {
    const data = lastMessageId ? { lastMessageId } : {};
    const response = await request(api.chats.getChat, {
      data,
      urlParams: { chatId },
    });
    return { response, lastMessageId };
  },
});

export const deleteMessage = createEffect({
  name: 'deleteMessage',
  async handler({ chatId, messageId }: { chatId: string; messageId: string }) {
    const response = await request(api.chats.deleteMessage, {
      urlParams: { chatId, messageId },
    });
    return { response, chatId, messageId };
  },
});

const preloaders = createPreloadersStore([getList, deleteMessage]);

type ChatState = {
  messages?: ITribesChatDetailMessages[];
  members?: ITribesMembers[] | null;
  tribeInfo?: ITribes;
  created_by?: { id: string } | null;
  [k: string]: unknown;
};

const chat = createStore<ChatState>({})
  .on(getList.done, (state, { result }) => {
    const responseObj = result.response as unknown as Record<string, unknown>;
    const body = get(responseObj, ['data', 'body'], {}) as unknown;

    const nextMessages = Array.isArray(body)
      ? (body as ITribesChatDetailMessages[])
      : (get(body as Record<string, unknown>, ['messages'], []) as ITribesChatDetailMessages[]);

    const nextState: ChatState = Array.isArray(body)
      ? { ...state, messages: nextMessages }
      : {
          ...state,
          ...(body && typeof body === 'object' ? (body as Record<string, unknown>) : {}),
          messages: nextMessages,
        };

    if (!result.lastMessageId) return nextState;

    return {
      ...nextState,
      messages: [...(state.messages ?? []), ...nextMessages],
    };
  })
  .on(deleteMessage.done, (state, { result }) => {
    const messageId = result.messageId;
    return {
      ...state,
      messages: (state.messages ?? []).map((m) =>
        String(m.id) !== String(messageId)
          ? m
          : {
              ...m,
              msgDeleted: true,
            },
      ),
    };
  })
  .reset(reset);

const chatDetail = combine({ chat, preloaders });
export default chatDetail;
